import React, { useEffect, useState } from 'react';
import { Col, Row, Alert, Button } from "react-bootstrap";
import { Dispatch, bindActionCreators, AnyAction } from 'redux';
import { connect } from 'react-redux';
import { accountActionCreators, AccountAction } from '../../store/AccountStore';
import { AppState } from '../../store/configureStore';
import { stateNeedsLoading } from '../../functions/stateNeedsLoading';
import Skeleton from "react-loading-skeleton";
import { driverPay_source } from '../../DCR.dtos';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretRight, faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { checkNullOrUndefined, getAccountFieldValueByName } from '../../functions/dcrGlobal';
import { AddUpdateAccount } from "../AddUpdateAccount";
import { Permissions } from '../../store/HelperStore';
import { helperActionCreators } from '../../store/HelperStore';
import { MessagePopup, MessageTypes } from "../MessagePopup";
import { encodeForOmni } from "../../functions/dcrGlobal";
import { hasPermission } from '../../functions/hasPermission';
import { DisplayBankInfo } from "../DisplayBankInfo";
import { UpdateContributionOptions } from '../UpdateContributionOptions';

type TManageAccountsProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;
const ManageAccounts = (props: TManageAccountsProps) => {

    const [showContributionOptionSection, setShowContributionOptionSection] = useState(false);
    const [showContributionOptionLink, setShowContributionOptionLink] = useState(false);
    const [showUpdateInfoLink, setShowUpdateInfoLink] = useState(false);
    const [displaySchedulePaymentMessage, setDisplaySchedulePaymentMessage] = useState(false);
    const [showAddUpdateAccount, setShowAddUpdateAccount] = useState(false);
    const [addEditRecord, setAddEditRecord] = useState<driverPay_source | null>(null);
    const [accountActionMode, setAccountActionMode] = useState(AccountAction.NONE);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
    const [showSingleDeleteWarning, setShowSingleDeleteWarning] = useState(false);
    const [deleteSelectedRecord, setDeleteSelectedRecord] = useState<driverPay_source | null>(null);
    const [showDeleteRecordError, setShowDeleteRecordError] = useState(false);
    const [showUpdateContributionOptions, setShowUpdateContributionOptions] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const updatePaymentSubmitClaim = hasPermission(Permissions.UpdatePayment_Submit, props.currentUser?.permissions);

    useEffect(() => {
        if (stateNeedsLoading(props.accounts)) {
            props.fetchBankInfo(props.dcr?.selectedInstitution);
        }
    });

    const getContributionOptionLink = () => {
        const paymentAccounts: driverPay_source[] = props.accounts?.cmAccounts?.pay_sources;

        if ((!paymentAccounts) || (paymentAccounts && paymentAccounts.length === 0)) {
            return false;
        }
        else {
            paymentAccounts.forEach((paymentAccount) => {
                const accountNumber = paymentAccount.source_info.find(x => x.name === "Account Number:")?.value?.replace(/%20/g, ' ');
                if (!checkNullOrUndefined(accountNumber) && accountNumber?.trim().length === 0) {
                    return false;
                }
            })
        }

        return (props.orgType === "E" || props.orgType === "P")
    }


    useEffect(() => {
        if (props.accounts?.isLoaded) {
            setShowContributionOptionLink(getContributionOptionLink());
            setShowContributionOptionSection(props.orgType === "E" || props.orgType === "P")
            setShowUpdateInfoLink(props.accounts?.cmAccounts?.pay_sources[0]?.acct_type?.toLowerCase() !== "wespath");
        }
    }, [props.accounts?.isLoaded])


    useEffect(() => {
        if (props.accounts.isOptionsLoaded) {
            setDisplaySchedulePaymentMessage(props.accounts.cmOptions?.contributionOptions?.scheduletype?.trim().length > 0);
        }
    }, [props.accounts?.isOptionsLoaded])

    useEffect(() => {
        if (props.accounts?.isAccountUpdated) {
            if (!props.accounts?.isUpdateAccountError) {
                setShowSuccessPopup(true);
                if (accountActionMode === AccountAction.DELETE) {
                    setIsDeleting(false);
                    setShowDeleteConfirmPopup(false);
                }
            }
            else {
                if (accountActionMode === AccountAction.DELETE) {
                    setIsDeleting(false);
                    setShowDeleteConfirmPopup(false);
                    setShowDeleteRecordError(true);
                }
            }
        }
    }, [props.accounts?.isAccountUpdated])



    const resetLocalState = (isUpdateBankInfo: boolean) => {
        setShowAddUpdateAccount(false);
        setAccountActionMode(AccountAction.NONE);
        setAddEditRecord(null);
        setShowSuccessPopup(false);
        if (isUpdateBankInfo) {
            props.fetchBankInfo(props.dcr?.selectedInstitution);
        }
        else {
            props.resetErrorState();
        }
    }

    const handleAccountAction = (mode: AccountAction, bankAccount: driverPay_source | null) => {
        if (mode === AccountAction.ADD || mode === AccountAction.EDIT) {
            setAccountActionMode(mode);
            setAddEditRecord(bankAccount);
            setShowAddUpdateAccount(true);
        }
        else if (mode === AccountAction.DELETE) {
            if (props.accounts?.cmAccounts?.pay_sources.length === 1) {
                setShowSingleDeleteWarning(true);
            }
            else {
                setDeleteSelectedRecord(bankAccount);
                setAccountActionMode(mode);
                setShowDeleteConfirmPopup(true);
            }
        }
    }

    const getInstitutionName = (): string => {
        if (props.currentUser && (!checkNullOrUndefined(props.currentUser?.activeInstitution))) {
            return props.currentUser?.institutions.find(x => x.value === props.dcr?.selectedInstitution)?.display ?? "";
        }
        return "";
    }

    const getBankName = (bankAccount: driverPay_source) => {
        return decodeURIComponent(bankAccount.source_info.find(x => (x.name === "Account Name:" || x.name === "Wespath Account Name:"))?.value ?? "");
    }

    const generateXML = () => {
        let xmlData = "";
        const strLastUpdated = props.currentUser?.userName;

        const strRootNodeWithAttribute = '<pay_source org_id="#valueRootAttribute1#" action="delete" sequence="#valueRootAttribute2#">';
        const strChildNodeWithAttribute = '<source_info id="#id#" name="#parameterName#" value="#value#" />';

        xmlData = strRootNodeWithAttribute.replace("#valueRootAttribute1#", props.dcr?.selectedInstitution)
            .replace("#valueRootAttribute2#", deleteSelectedRecord?.sequence?.toString()?.padStart(3, "0") ?? "");

        const bankNameNode = strChildNodeWithAttribute.replace("#id#", "1").replace("#parameterName#", "Bank Name:").replace("#value#", encodeForOmni(getAccountFieldValueByName(deleteSelectedRecord, "Bank Name:")));
        const phoneNumberNode = strChildNodeWithAttribute.replace("#id#", "2").replace("#parameterName#", "Phone Number:").replace("#value#", getAccountFieldValueByName(deleteSelectedRecord, "Phone Number:")?.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3"));
        const accountNameNode = strChildNodeWithAttribute.replace("#id#", "3").replace("#parameterName#", "Account Name:").replace("#value#", encodeForOmni(getAccountFieldValueByName(deleteSelectedRecord, "Account Name:~Wespath Account Name:")));
        const accountNumberNode = strChildNodeWithAttribute.replace("#id#", "4").replace("#parameterName#", "Account Number:").replace("#value#", getAccountFieldValueByName(deleteSelectedRecord, "Account Number:"));
        const accountTypeNode = strChildNodeWithAttribute.replace("#id#", "5").replace("#parameterName#", "Account Type:").replace("#value#", getAccountFieldValueByName(deleteSelectedRecord, "Account Type:"));
        const transitRoutingNumberNode = strChildNodeWithAttribute.replace("#id#", "6").replace("#parameterName#", "Transit Routing Number:").replace("#value#", getAccountFieldValueByName(deleteSelectedRecord, "Transit Routing Number:"));
        const lastUpdatedNode = strChildNodeWithAttribute.replace("#id#", "7").replace("#parameterName#", "updatedby:").replace("#value#", strLastUpdated);

        xmlData = xmlData.concat(bankNameNode)
            .concat(phoneNumberNode)
            .concat(accountNameNode)
            .concat(accountNumberNode)
            .concat(accountTypeNode)
            .concat(transitRoutingNumberNode)
            .concat(lastUpdatedNode);

        xmlData = xmlData.concat("<cca_list>");
        if (props.historyFiles && props.historyFiles?.length > 0) {
            props.historyFiles.forEach((file) => {
                if (file.status === "Pending") {
                    xmlData = xmlData.concat("<cca>" + file.cca_id + "</cca>");
                }
            });
        }
        xmlData = xmlData.concat("</cca_list>");
        xmlData = xmlData.concat("</pay_source>");
        return xmlData;
    }

    const deleteBankRecord = () => {
        setIsDeleting(true);
        const strXML = generateXML();
        props.updateBankAccountInfo(strXML);
    }

    const cancelDelete = () => {
        setDeleteSelectedRecord(null);
        setAccountActionMode(AccountAction.NONE);
        setShowDeleteConfirmPopup(false);
    }


    const GetBankDetails = (props: { bankAccount: driverPay_source; itemIndex: number }) => {
        const [expand, setExpand] = useState(false);
        const displayWespathMsg = props.bankAccount?.source_info?.find(x => x.name === "Wespath Account Number:") !== undefined;

        return (
            <>
                <Row>
                    <Col className="col-12">
                        <span style={{ marginTop: "4px" }}>
                            <a style={{ cursor: "pointer"}} onClick={() => { setExpand(!expand); }}>
                                <span className="text-nowrap">
                                    <FontAwesomeIcon icon={expand ? faCaretDown : faCaretRight} className="mr-1" />
                                    <span style={{ fontWeight: "bold" }}>{getBankName(props.bankAccount)}</span>
                                </span>
                            </a>
                        </span>
                        <span className="pl-5">
                            {displayWespathMsg && "Contact Wespath to change"}
                            {(!displayWespathMsg && !isDeleting) && <>  
                                <Button className="m-0 p-0 mr-3" type="button" variant="link" onClick={() => { handleAccountAction(AccountAction.EDIT, props.bankAccount) }}>Edit</Button>
                                <Button className="m-0 p-0" type="button" variant="link" onClick={() => { handleAccountAction(AccountAction.DELETE, props.bankAccount) }}>Delete</Button>
                            </> }
                            {(!displayWespathMsg && isDeleting) && <LoadingSkeletonButtonBar />}
                        </span>
                    </Col>
                </Row>
                {expand &&
                    <Row>
                        <Col className="col-12 col-md-6">
                            <DisplayBankInfo pmtAccount={props.bankAccount} />
                        </Col>
                    </Row>
                }
            </>
        );
    }

    const getFileTypeMessage = (): string => {
        const _FileType_ebill_message = "Your organization's monthly contribution files are automatically generated by Wespath. You may choose to upload your own through the link above.";
        const _FileType_payroll_message = "Your organization's contribution files are automatically generated by your payroll provider,";

        let fileTypeMessage = "";

        if (props.accounts?.cmOptions?.contributionOptions?.filetype?.toLowerCase() === "payroll") {
            fileTypeMessage = _FileType_payroll_message + " " + props.accounts?.cmOptions?.contributionOptions?.payrollname + ".";
        }
        else {
            fileTypeMessage = _FileType_ebill_message;
        }

        return fileTypeMessage;
    }

    const getScheduleTypeMessage = (): string => {
        const _ScheduleType_manual_message = "Your organization has chosen to manually schedule payments for contributions. You may change your election to automatic payments by selecting \"Update Contribution Options\".";
        const _ScheduleType_auto_message = "Your organization has chosen automatic payments for contributions. You may change your election to manually scheduled payments by selecting \"Update Contribution Options\".";

        let scheduleTypeMessage = "";

        if (props.accounts?.cmOptions?.contributionOptions?.scheduletype?.toLowerCase() === "manual") {
            scheduleTypeMessage = _ScheduleType_manual_message;
        }
        else if (props.accounts?.cmOptions?.contributionOptions?.scheduletype?.toLowerCase() === "auto") {
            scheduleTypeMessage = _ScheduleType_auto_message;
        }
        else {
            scheduleTypeMessage = "";
        }

        return scheduleTypeMessage;
    }

    const getDeleteErrorMessageDisplayType = (): MessageTypes => {
        let msgType = MessageTypes.Error
        if (!checkNullOrUndefined(props.accounts?.updateAccountErrorMessage) && (props.accounts?.updateAccountErrorMessage?.includes("|"))) {
            const msgTypeIndicator = props.accounts?.updateAccountErrorMessage?.split("|")[0];
            if (!checkNullOrUndefined(msgTypeIndicator) && msgTypeIndicator?.toUpperCase() === "W")
                msgType = MessageTypes.Warning;
        }
        return msgType;
    }

    const getDeleteErrorMessageText = (): string => {
        let msgText = props.accounts?.updateAccountErrorMessage;
        if (msgText && msgText.includes("|")) {
            msgText = props.accounts?.updateAccountErrorMessage?.split("|")[1];
        }
        return msgText ?? "";
    }

    return (
        <>
            <Row className="mb-2">
                <Col>
                    <div style={{ paddingTop: "1rem" }}>
                        <div className="col-md-12 overflow-auto" style={{ float: "left" }}>
                            {(!showAddUpdateAccount && !showUpdateContributionOptions) &&
                                <>
                                    {props.accounts?.error && < Alert className="mt-2" variant="danger">Unable to fetch Account Details: {props.accounts.errorMessage}</Alert>}

                                    {props.accounts?.isLoading && <LoadingSkeletonPaymentOptions />}

                                    {(props.accounts?.isLoaded && !props.accounts?.error) &&
                                        <>
                                            <Row className="tbl-info-wrap">
                                                <Col className="float-left col-12 col-md-8 col-lg-9">
                                                    <h4>{getInstitutionName()} Payment Information</h4>
                                                </Col>
                                                {showUpdateInfoLink &&
                                                    <Col className="float-right col-12 col-md-4 col-lg-3 mr-0 pr-0 text-right">
                                                        <Button type="button" variant="link" onClick={() => { handleAccountAction(AccountAction.ADD, null) }}>Update Payment Account</Button>
                                                    </Col>
                                                }
                                            </Row>
                                            {props.accounts?.cmAccounts.pay_sources.map((bankAccount, i) =>
                                                <div key={i}>
                                                    <GetBankDetails bankAccount={bankAccount} itemIndex={i} />
                                                </div>
                                            )}
                                            {(!showUpdateInfoLink) &&
                                                <>
                                                    <Row>
                                                        <Col className="col-xs-12">
                                                            <div className="text-left small" style={{ paddingTop: "30px", paddingBottom: "20px" }}>
                                                                <strong>Note:</strong>  If you would like to change the account used to fund contributions, contact us for assistance.
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </>
                                            }
                                            <Row>
                                                <Col>
                                                    <hr />
                                                </Col>
                                            </Row>
                                        </>
                                    }

                                    {(props.accounts?.isLoading || props.accounts?.isOptionsLoading) && <LoadingSkeletonContributionOptions />}

                                    {props.accounts?.isOptionsLoaded && !props.accounts?.error && (showContributionOptionSection) &&
                                        <>
                                            <Row className="tbl-info-wrap mt-4">
                                                <Col className="text-left">
                                                    <h4>{getInstitutionName()} Contribution Options</h4>
                                                </Col>
                                                {showContributionOptionLink &&
                                                    <Col className="float-right col-12 col-md-5 col-lg-4 col-xl-3 mr-0 pr-0 text-right">
                                                        <Button type="button" variant="link" onClick={() => { setShowUpdateContributionOptions(true); }}>Update Contribution Options</Button>
                                                    </Col>
                                                }
                                            </Row>
                                            <Row>
                                                <Col className="col-xs-12 text-muted">
                                                    <strong><label className="text-left mb-0">File Generation</label></strong>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col className="col-xs-12">
                                                    <div className="text-left">{getFileTypeMessage()}</div>
                                                </Col>
                                            </Row>
                                            {displaySchedulePaymentMessage &&
                                                <>
                                                    <Row className="mt-3">
                                                        <Col className="col-xs-12 text-muted">
                                                            <strong><label className="text-left mb-0">Recurring Payment Option</label></strong>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col className="col-xs-12">
                                                            <div className="text-left">{getScheduleTypeMessage()}</div>
                                                        </Col>
                                                    </Row>
                                                </>
                                            }
                                        </>
                                    }
                                </>
                            }

                            {showAddUpdateAccount &&
                                <AddUpdateAccount
                                    showAddUpdate={showAddUpdateAccount}
                                    onClose={() => { setShowAddUpdateAccount(false) }}
                                    actionMode={accountActionMode}
                                    bankDetails={addEditRecord}
                                    onVerifyCancel={(isFromSubmit: any) => { isFromSubmit ? setShowSuccessPopup(true) : resetLocalState(false) }}
                                />
                            }

                            {showSuccessPopup &&
                                <MessagePopup showPopup={showSuccessPopup} popupTitle={"Contribution Management"} messageType={MessageTypes.Success} okButtonText={"OK"} onOkClick={() => { resetLocalState(true); }}>
                                    {`You have successfully ${(accountActionMode === AccountAction.ADD || accountActionMode === AccountAction.EDIT) ? "updated" : "deleted"} your organization's payment information. You will receive a confirmation via e-mail.`}
                                </MessagePopup>
                            }

                            {showDeleteConfirmPopup &&
                                <MessagePopup showPopup={showDeleteConfirmPopup} showCancelButton={true}
                                    popupTitle={"Delete Bank Account"} messageType={MessageTypes.Warning}
                                    okButtonText={"Submit"} enableOK={updatePaymentSubmitClaim} onOkClick={() => { deleteBankRecord() }}
                                    onCancelClick={() => { cancelDelete() }}
                                >
                                    {`Are you sure you want to delete this payment account?`}
                                </MessagePopup>
                            }

                            {showSingleDeleteWarning &&
                                <MessagePopup showPopup={showSingleDeleteWarning} popupTitle={"Error"} messageType={MessageTypes.Error} okButtonText={"Ok"} onOkClick={() => { setShowSingleDeleteWarning(false) }}>
                                    {`You cannot delete the only payment account on file. You must either edit your current payment account or add a new payment account prior to deleting this account.`}
                                </MessagePopup>
                            }

                            {showDeleteRecordError &&
                                <MessagePopup showPopup={showDeleteRecordError} popupTitle={"Delete Bank Account Error"} messageType={getDeleteErrorMessageDisplayType()}
                                    okButtonText={"Ok"} onOkClick={() => { props.resetErrorState(); setShowDeleteRecordError(false) }}>
                                    {`Error while deleting record : ${getDeleteErrorMessageText()}`}
                                </MessagePopup>
                            }

                            {showUpdateContributionOptions &&
                                <UpdateContributionOptions showUpdateContributionOption={showUpdateContributionOptions}
                                    orgType={props.orgType}
                                    paymentAccounts={props.accounts?.cmAccounts?.pay_sources}
                                    selectedScheduleType={props.accounts?.cmOptions?.contributionOptions?.scheduletype}
                                    selectedAccontSequece={props.accounts.cmOptions?.contributionOptions?.selected}
                                    onClose={(e: any) => setShowUpdateContributionOptions(false)} />
                            }
                        </div>
                    </div>
                </Col>
            </Row>
        </>
    )
};

const LoadingSkeletonPaymentOptions = () => (
    <div className="col-12 pl-0">
        <div className="mt-2"><Skeleton width={'95%'} /></div>
        <div><Skeleton width={'100%'} /></div>
        <div className="mt-2"><Skeleton width={'95%'} /></div>
        <div><Skeleton width={'100%'} /></div>
        <div className="mt-2"><Skeleton width={'95%'} /></div>
        <div><Skeleton width={'100%'} /></div>
        <div className="mt-2"><Skeleton width={'95%'} /></div>
        <div><Skeleton width={'100%'} /></div>
    </div>
);

const LoadingSkeletonContributionOptions = () => (
    <div className="col-12 pl-0">
        <div className="mt-2"><Skeleton width={'95%'} /></div>
        <div><Skeleton width={'100%'} /></div>
        <div className="mt-2"><Skeleton width={'95%'} /></div>
        <div><Skeleton width={'100%'} /></div>
        <div className="mt-2"><Skeleton width={'95%'} /></div>
        <div><Skeleton width={'100%'} /></div>
    </div>
);

const LoadingSkeletonButtonBar = () => (
    <>
        <Row className="my-1 pt-2 d-flex">
            <div><Skeleton width={'15em'} /></div>
            <div><Skeleton width={'15em'} /></div>
        </Row>
    </>
);

export interface PaymentAccountDrowDownProps {
    paymentAccounts: driverPay_source[]
}

function mapStateToProps(state: AppState) {
    return {
        dcr: state.dcr,
        currentUser: state.dcr.currentuserInfo?.user,
        accounts: state.accounts,
        historyFiles: state.cmHistory?.cmHistoryInfo?.cntb_history?.files,
        orgType: state.cmHistory?.cmHistoryInfo?.cntb_history?.org_type.toUpperCase()
    }
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators({ ...accountActionCreators, ...helperActionCreators }, dispatch);
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ManageAccounts);